<template>
	<page-container :page="page">
		<main-content
			:title="page.title || page.header"
			:subtitle="page.subtitle"
			:content="page.content"
			:booking-id="page.bookingID"
		/>

		<section class="main-content rooms-section background-light">
			<div class="row row-header align-center">
				<h2 class="subtitle">{{ defaults[locale].rooms.subtitle }}</h2>
				<h2>{{ defaults[locale].rooms.sectionRoomsTitle }}</h2>
				<div v-parse-links v-html="defaults[locale].rooms.sectionRoomsContent" />
			</div>
			<div
				v-for="(room, index) in roomsData"
				:key="room.pageID"
				class="row contentblock"
				:class="{ right: index % 2 == 0, left: index % 2 != 0 }"
			>
				<div
					class="columns column6 contentblock-content"
					:class="{ right: index % 2 == 0, left: index % 2 != 0 }"
				>
					<h2>{{ room.title || room.header }}</h2>
					<hr />
					<div v-parse-links v-html="room.content" />
					<div class="room-icons">
						<div v-for="facility in room.facilities" :key="facility.id" class="icon-holder">
							<font-awesome-icon :icon="`fal fa-${facility.icon}`" size="1x" />
							<span>{{ facility.content }}</span>
						</div>
					</div>
					<nuxt-link :to="localePath(`/${room.filename}`)" class="button button-no-fill">
						{{ defaults[locale].rooms.view }}
					</nuxt-link>
					<book-button class="button button-cta">
						<span>{{ defaults[locale].rooms.book }}</span>
					</book-button>
				</div>
				<div class="side-curve" :class="{ right: index % 2 == 0, left: index % 2 != 0 }" />
				<div class="contentblock-image">
					<picture>
						<source v-if="room.imageOverviewWebp" :srcset="room.imageOverviewWebp" type="image/webp" />
						<source :srcset="room.imageOverview" />
						<img loading="lazy" :src="room.imageOverview" :alt="room.imageOverviewAlt" />
					</picture>
				</div>
			</div>
		</section>
	</page-container>
</template>

<script setup>
const localePath = useLocalePath();
const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();

await fetchPage();
await fetchDefaults();

const { data: roomsData } = await useWebsiteFetch('rooms', {
	query: { language: locale.value },
	key: `${locale.value}/rooms`,
});
</script>

<style lang="scss" scoped>
.background-light {
	background: $alternating-background-color;
}

.contentblock {
	margin: 40px auto;
	position: relative;
	display: flex;
	flex-flow: row nowrap;
	justify-content: stretch;
	min-height: 540px;
	width: 100%;
	float: none;
	overflow: hidden;

	&.right {
		flex-flow: row-reverse nowrap;
	}
}

.row-header {
	max-width: 1000px;
}

.contentblock-image {
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	width: 58%;
	z-index: 1;
	float: none;
}

.contentblock-content {
	background: #d6d1c4;
	padding: 60px 70px 50px 20px;
	margin: 0;
	position: relative;
	z-index: 2;
	float: none;
	width: calc(42% - 1px);
	display: flex;
	flex-flow: column wrap;
	justify-content: center;

	&.left {
		padding: 60px 20px 50px 70px;
	}
}

.row:nth-child(even) .contentblock-image {
	left: 0;
	right: auto;
}

.contentblock-image img {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.side-curve {
	width: 300px;
	background: url('~/assets/images/benefits-bg-side-left.svg') no-repeat top right;
	background-size: cover;
	float: none;
	position: absolute;
	right: calc(42% - 1px);
	top: 0;
	bottom: -2px;
	z-index: 10;

	&.left {
		background: url('~/assets/images/benefits-bg-side.svg') no-repeat top right;
		background-size: cover;
		left: calc(42% - 1px);
	}
}

.room-icons {
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-start;
	align-items: flex-start;
	margin: 0 0 30px;
}

.icon-holder {
	color: $footer-background-color;
	width: 49%;
	margin: 5px 0;
}

.icon-holder i,
.icon-holder svg {
	margin: 0 5px 0 0;
}

.room-item .room-icons {
	max-width: 600px;
}

.room-info {
	padding: 0 0 50px;

	.row {
		padding: 50px 30px;
	}
}

@media (max-width: 1080px) {
	.icon-holder {
		width: 49%;
	}
}

@media (max-width: 920px) {
	.contentblock-image {
		width: 100%;
		position: relative;
		inset: auto !important;
	}

	.contentblock,
	.contentblock.right {
		display: flex;
		flex-flow: wrap column-reverse;
		min-height: inherit;
		max-height: inherit;
	}

	.contentblock-content.left,
	.contentblock-content.right {
		margin: 0 auto 50px;
		padding: 0 30px 50px;
		width: 100%;
	}

	.side-curve,
	.side-curve.left {
		width: 100%;
		height: 200px;
		margin-top: -165px;
		z-index: 10;
		background: url('~/assets/images/rooms-curve-top.svg') no-repeat top right;
		background-size: cover;
		position: relative;
		inset: auto;
	}

	.icon-holder {
		width: 49%;
	}
}

@media (max-width: 680px) {
	.icon-holder {
		width: 49%;
	}
}
</style>
